import { createAsyncThunk, createEntityAdapter, createSlice, current, nanoid } from '@reduxjs/toolkit';
import { JSONApiDataObject, MediaAttachment, Message, ReduxEntity } from 'Douleutaras/types';
import { RootState } from 'Douleutaras/redux/store';
import { makeRequest } from 'Douleutaras/services/apiClient';
import normalize from 'json-api-normalizer';
import { usersSlice } from 'Douleutaras/redux/data/users/usersSlice';
import { conversationsSlice } from 'Douleutaras/redux/data/conversations/conversationsSlice';
import { localMediaAttachmentsAdded, mediaAttachmentsSlice } from 'Douleutaras/redux/data/conversations/mediaAttachmentsSlice';
import { deepMergeMetaData } from 'Douleutaras/utilities/redux/merge';

export const messagesAdapter = createEntityAdapter<JSONApiDataObject<Message>>({
  selectId: message => message.id
});

export const fetchMessages = createAsyncThunk(
  'messages/getAll',
  async ({ conversationId, page, pageSize = 30, fetchOnTopOfList = false }
  : { conversationId: number, page?: number, pageSize?: number, fetchOnTopOfList?: boolean },
         api) => {
    const endpoint = `api/public/v3/conversation/${conversationId}/auto-scroll-unseen-messages/`;
    const additionalGetParams = {
      sort: '-created',
      'page[size]': pageSize
    };

    if (page) {
      additionalGetParams['page[number]'] = page;
    }

    const response = await makeRequest(endpoint, {
      method: 'GET',
      additionalGetParams,
      includes: [ 'mediaAttachments' ]
    })(api);

    return { ...handleMessagesResponse({
        response: response.data,
        conversationId,
        endpoint,
        api,
        currentPage: page // current page that is being loaded
    }), conversationId, fetchOnTopOfList };
  }
);

const handleMessagesResponse = ({ response, conversationId, endpoint, api, currentPage }) => {
  const normalizedResponse = normalize(response, { endpoint });
  const { usersAdded } = usersSlice.actions;
  const { mediaAttachmentsAdded } = mediaAttachmentsSlice.actions;

  if (normalizedResponse.mediaAttachments) {
    api.dispatch(
      mediaAttachmentsAdded(normalizedResponse.mediaAttachments)
    );
  }

  if (normalizedResponse.users) {
    api.dispatch(
      usersAdded(normalizedResponse.users)
    );
  }

  return normalizedResponse;
};

const RETRY_IN_SECONDS_IF_MESSAGE_FAILED = 10000;

const remoteAddCatch = ({ text, conversationId, uuid, attachments }, api) => error => {
  // In case the user is offline or sending failed, retry in XXX seconds
  setTimeout(() =>
      api.dispatch(addRemoteMessage({ text, conversationId, uuid, attachments }))
        .unwrap()
        .catch(remoteAddCatch({ text, conversationId, uuid, attachments }, api))
    , RETRY_IN_SECONDS_IF_MESSAGE_FAILED);
};

export const addMessage = createAsyncThunk(
  'message/add',
  async (
    {
      text,
      conversationId,
      senderId,
      attachments = []
    }: { text: string, conversationId: number, senderId: number, attachments?: [] },
    api) => {
    const uuid = nanoid();

    await api.dispatch(addLocalMessage({ text, conversationId, senderId, uuid, attachments }));
    await api.dispatch(addRemoteMessage({ text, conversationId, uuid, attachments }))
      .unwrap()
      .catch(remoteAddCatch({ text, conversationId, uuid, attachments }, api));

    return {};
  }
);

// When an event is received, like from pusher, the action will check whether it should bump a conversation to the top
// of the listing or not
export const checkAndUpdateConversationListOrdering = createAsyncThunk(
  'message/checkAndUpdateConversationList',
  ({ conversationId }: { conversationId: number }, api) => {
    const { bumpConversationToTop, updateConversationLastMessageTimestamp }
      = conversationsSlice.actions;

    // Put conversation on the top of the conversation list
    api.dispatch(bumpConversationToTop({
      conversationId
    }));

    // Update timestamp of "last message"
    api.dispatch(updateConversationLastMessageTimestamp({
      conversationId
    }));
  });

export const addLocalMessage = createAsyncThunk(
  'message/localStore',
  ({ text, conversationId, senderId, uuid, attachments = [], noLastMessage = false }:
     { text: string,
       conversationId: number,
       senderId: number,
       uuid: string,
       attachments?: Array<MediaAttachment>,
       noLastMessage?: boolean // Don't add last message
     },
   api) => {

    const { lastMessageAdded, bumpConversationToTop, updateConversationLastMessageTimestamp }
      = conversationsSlice.actions;

    if (!noLastMessage) {
      // Update the "last message" of the conversation
      api.dispatch(lastMessageAdded({
        lastMessage: {
          conversationId,
          content: text
        }
      }));
    }

    api.dispatch(checkAndUpdateConversationListOrdering({ conversationId }));

    let relationships = {};

    if (attachments) {
      const attachmentsEntity = attachments.map(attachment => ({
        id: nanoid(),
        type: 'mediaAttachments',
        attributes: attachment
      }));

      relationships = {
        mediaAttachments: {
          data: attachmentsEntity.map(attachmentEntity => ({
            id: attachmentEntity.id,
            type: attachmentEntity.type
          }))
        }
      };
      const { mediaAttachmentsAdded } = mediaAttachmentsSlice.actions;
      api.dispatch(mediaAttachmentsAdded(
        attachmentsEntity
      ));
      api.dispatch(localMediaAttachmentsAdded({ conversationId, attachmentsEntity }));

    }

    return {
      conversationId,
      uuid,
      messageObject: {
        id: uuid,
        type: 'messages',
        attributes: {
          content: text,
          sentById: senderId,
          conversationId,
          messageType: 'local',
          created: new Date().toString(),
          modified: '',
          archived: false
        },
        relationships,
        links: {}
      } as JSONApiDataObject<Message>
    };
  }
);

export const generateMessagesEndpoint = (conversationId: number | string) => {
  return `api/public/v3/conversation/${conversationId}/auto-scroll-unseen-messages/`;
};

export const addRemoteMessage = createAsyncThunk(
  'message/remoteStore',
  async (
    { text, conversationId, uuid, attachments = [] }
    : { text: string, conversationId: number, uuid: string, attachments?: Array<MediaAttachment> },
    api) => {
    const endpoint = generateMessagesEndpoint(conversationId);

    const response = await makeRequest(endpoint, {
      method: 'POST',
      includes: [ 'mediaAttachments' ],
      body: {
        data: {
          type: 'messages',
          attributes: {
            archived: false,
            content: text,
            messageType: 'instant',
            attachments
          }
        }
      }
    })(api);

    handleMessagesResponse({ response: response.data, endpoint, api });

    return { response: response.data, uuid, conversationId };
  }
);

export const messagesSlice = createSlice({
  name: 'messages',
  initialState: messagesAdapter.getInitialState({
    loading: undefined,
    error: false,
    meta: {},
    entities: {}
  } as ReduxEntity<JSONApiDataObject<Message>>),
  reducers: {
    setAllMessagesAsSeen: (state, action) => {
      Object.keys(state.entities)
        .map(messageId => {
          state.entities[messageId].attributes.seenBy = {
            userId: parseInt(action.payload.participantUserId, 0)
          };
        });
    }
  },
  extraReducers: builder => {
    builder.addCase(addRemoteMessage.fulfilled, (state, action) => {
      const messagesMetaEndpoint = generateMessagesEndpoint(action.payload.conversationId);
      const currentMetaMessages = state.meta[messagesMetaEndpoint].data;

      state.meta[messagesMetaEndpoint].data = currentMetaMessages.map(message => {
        if (action.payload.uuid === message.id) {

          return generateMetaMessage({
            id: action.payload.response.data.id,
            type: action.payload.response.data.type
          });
        }

        return message;
      });

      messagesAdapter.removeOne(state, action.payload.uuid);
      messagesAdapter.addOne(state, action.payload.response.data);

    });

    builder.addCase(fetchMessages.fulfilled, (state, action) => {
      const endpoint = generateMessagesEndpoint(action.payload.conversationId);

      messagesAdapter.upsertMany(state, action.payload.messages || {});

      // @ts-ignore
      state = deepMergeMetaData(state, action, endpoint, action.payload.fetchOnTopOfList);
      sortMetaDataByMessageCreated(state, endpoint);

      // Store loaded pages inside a "loadedPages" array in "pagination" meta object. so they don't get loaded twice
      if (state.meta?.[endpoint]?.meta !== undefined) {
        const currentPage = state.meta[endpoint].meta.pagination.page;
        // Store loaded pages so we don't load them again
        if (!Array.isArray(state.meta?.[endpoint].meta.pagination.loadedPages)) {
          state.meta[endpoint].meta.pagination.loadedPages = [];
        }
        // If the page is already loaded, don't push it again into the array
        if (!state?.meta[endpoint]?.meta?.pagination?.loadedPages?.includes(currentPage)) {
          state.meta[endpoint].meta.pagination.loadedPages.push(currentPage);
        }
      }
    });

    builder.addCase(addLocalMessage.fulfilled, (state, action) => {
      messagesAdapter.upsertOne(state, action.payload.messageObject);
      const messagesMetaEndpoint = generateMessagesEndpoint(action.payload.conversationId);
      // Push into the array of messages, so it can be looped and read
      if (typeof state.meta[messagesMetaEndpoint] === 'undefined') {
        state.meta[messagesMetaEndpoint] = {
          data: []
        };
      }
      // Push into messages META
      state.meta[messagesMetaEndpoint].data.unshift(generateMetaMessage({
        id: action.payload.uuid,
        type: 'messages'
      }));
      sortMetaDataByMessageCreated(state, messagesMetaEndpoint);
    });
  }
});

interface MetaMessage {
  id: string;
  type: string;
}

const generateMetaMessage = ({ id, type }: MetaMessage) => {
  return {
    id,
    type
  };
};

const sortMetaDataByMessageCreated = (state, endpoint: string) => {
  const endpointMeta = state.meta?.[endpoint];
  if (!endpointMeta?.data) {
    return;
  }

  endpointMeta.data.sort((messageMetaA, messageMetaB) => {
    const messageA = state.entities[messageMetaA.id];
    const messageB = state.entities[messageMetaB.id];
    const createdA = messageA?.attributes?.created;
    const createdB = messageB?.attributes?.created;
    const timestampA = createdA ? Date.parse(createdA) : 0;
    const timestampB = createdB ? Date.parse(createdB) : 0;

    if (timestampA === timestampB) {
      return String(messageMetaB.id)
        .localeCompare(String(messageMetaA.id));
    }

    return timestampB - timestampA;
  });
};

export const messagesSelectors = messagesAdapter
  .getSelectors((state: RootState) => state.messages);
